import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getFileName, getFileIcon } from '../utils/fileUtils';
import './FileSelector.css';

/**
 * Component for selecting files to work with
 * @param {Object} props - Component props
 * @param {Array} props.files - List of available files
 * @param {string} props.selectedFile - Selected file ID
 * @param {Function} props.onFileChange - Handler for file change
 * @param {boolean} props.showSearch - Whether to show search filter
 */
function FileSelector({ files, selectedFile, onFileChange, showSearch }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Handle search input
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle filter type change
  const handleFilterChange = (e) => {
    setFilterType(e.target.value);
  };
  
  // Filter files based on search term and file type
  const filteredFiles = files.filter(file => {
    const matchesSearch = searchTerm === '' || 
      file.path.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesType = true;
    if (filterType !== 'all') {
      const extension = file.path.split('.').pop().toLowerCase();
      
      switch (filterType) {
        case 'js':
          matchesType = extension === 'js' || extension === 'jsx';
          break;
        case 'ts':
          matchesType = extension === 'ts' || extension === 'tsx';
          break;
        case 'web':
          matchesType = ['html', 'css', 'scss', 'less'].includes(extension);
          break;
        case 'doc':
          matchesType = ['md', 'txt', 'json', 'yaml', 'yml'].includes(extension);
          break;
        default:
          matchesType = extension === filterType;
      }
    }
    
    return matchesSearch && matchesType;
  });
  
  // Sort files by path
  const sortedFiles = [...filteredFiles].sort((a, b) => a.path.localeCompare(b.path));
  
  return (
    <div className="file-selector">
      {showSearch && (
        <div className="file-filter">
          <div className="search-container">
            <input 
              type="text"
              className="search-input"
              placeholder="Search files..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          <div className="filter-container">
            <select 
              className="filter-select"
              value={filterType}
              onChange={handleFilterChange}
            >
              <option value="all">All Files</option>
              <option value="js">JavaScript</option>
              <option value="ts">TypeScript</option>
              <option value="web">Web (HTML/CSS)</option>
              <option value="doc">Documents</option>
              <option value="py">Python</option>
              <option value="java">Java</option>
            </select>
          </div>
        </div>
      )}
      
      <div className="file-list-container">
        {sortedFiles.length > 0 ? (
          <ul className="file-select-list">
            {sortedFiles.map(file => (
              <li 
                key={file.id}
                className={`file-select-item ${file.id === selectedFile ? 'selected' : ''}`}
                onClick={() => onFileChange(file.id)}
              >
                <div className="file-select-icon">
                  {getFileIcon(file.language)}
                </div>
                <div className="file-select-details">
                  <div className="file-select-name">{getFileName(file.path)}</div>
                  <div className="file-select-path">{file.path}</div>
                </div>
                <div className="file-select-info">
                  <span className="file-size">{file.actualLoc || file.estimatedLoc} LOC</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="no-files-message">
            {searchTerm ? 'No files found matching your search' : 'No files available'}
          </div>
        )}
      </div>
    </div>
  );
}

FileSelector.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired,
      language: PropTypes.string,
      estimatedLoc: PropTypes.number,
      actualLoc: PropTypes.number
    })
  ).isRequired,
  selectedFile: PropTypes.string,
  onFileChange: PropTypes.func.isRequired,
  showSearch: PropTypes.bool
};

FileSelector.defaultProps = {
  showSearch: true
};

export default FileSelector;
